import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  MessageSquare, HelpCircle, Loader2, Check, Trash2, Plus, Edit,
  Mail, Phone, Calendar, Eye, EyeOff, ChevronDown, ChevronUp,
  Save, ArrowLeft, CalendarDays, Clock, User, Car, FileText,
  CheckCircle, XCircle, AlertCircle, Search, Bot, Sparkles, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Messages Tab
export const MessagesTab = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/admin/messages/${id}/read`);
      setMessages(messages.map(m => m.id === id ? { ...m, read: true } : m));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Naozaj chcete vymazať túto správu?')) return;
    try {
      await axios.delete(`${API}/admin/messages/${id}`);
      setMessages(messages.filter(m => m.id !== id));
      if (selectedMessage?.id === id) setSelectedMessage(null);
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Správy</h1>
        <span className="text-zinc-400 text-sm">
          {messages.filter(m => !m.read).length} neprečítaných
        </span>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Messages List */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="max-h-[600px] overflow-y-auto">
            {messages.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">
                Žiadne správy
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  data-testid={`message-${message.id}`}
                  onClick={() => {
                    setSelectedMessage(message);
                    if (!message.read) markAsRead(message.id);
                  }}
                  className={`p-4 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${
                    selectedMessage?.id === message.id ? 'bg-white/5' : ''
                  } ${!message.read ? 'border-l-2 border-l-[#00BFFF]' : ''}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{message.name}</p>
                      <p className="text-sm text-zinc-400 truncate">{message.email}</p>
                      <p className="text-sm text-zinc-500 mt-1 line-clamp-2">
                        {message.message}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {!message.read && (
                        <span className="w-2 h-2 bg-[#00BFFF] rounded-full" />
                      )}
                      <span className="text-xs text-zinc-500">
                        {new Date(message.created_at).toLocaleDateString('sk-SK')}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Message Detail */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          {selectedMessage ? (
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold">{selectedMessage.name}</h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-zinc-400">
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {selectedMessage.email}
                    </span>
                    {selectedMessage.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {selectedMessage.phone}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  data-testid="delete-message-btn"
                  onClick={() => deleteMessage(selectedMessage.id)}
                  className="p-2 hover:bg-red-500/10 text-zinc-400 hover:text-red-400 rounded-lg transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <Calendar className="w-4 h-4" />
                {new Date(selectedMessage.created_at).toLocaleString('sk-SK')}
              </div>

              <div className="border-t border-white/10 pt-6">
                <p className="text-zinc-300 whitespace-pre-wrap">
                  {selectedMessage.message}
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <a
                  href={`mailto:${selectedMessage.email}`}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#00BFFF] text-[#05050A] font-semibold rounded-lg hover:bg-[#33CCFF] transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  Odpovedať
                </a>
                {selectedMessage.phone && (
                  <a
                    href={`tel:${selectedMessage.phone}`}
                    className="flex-1 flex items-center justify-center gap-2 py-3 border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    Zavolať
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-zinc-500">
              Vyberte správu pre zobrazenie detailov
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Chats Tab - AI Chat History
export const ChatsTab = () => {
  const [chats, setChats] = useState([]);
  const [stats, setStats] = useState({ total_messages: 0, total_sessions: 0, recent_messages: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchChats();
    fetchStats();
  }, []);

  const fetchChats = async () => {
    try {
      const response = await axios.get(`${API}/admin/chats`);
      setChats(response.data);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/chats/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching chat stats:', error);
    }
  };

  const deleteChat = async (sessionId) => {
    if (!window.confirm('Naozaj chcete vymazať túto konverzáciu?')) return;
    try {
      await axios.delete(`${API}/admin/chats/${sessionId}`);
      setChats(chats.filter(c => c.session_id !== sessionId));
      if (selectedChat?.session_id === sessionId) setSelectedChat(null);
      fetchStats();
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('sk-SK', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredChats = chats.filter(chat => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return chat.messages.some(m => 
      m.user?.toLowerCase().includes(term) || 
      m.bot?.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Bot className="w-6 h-6 text-purple-400" />
            AI Chat História
          </h1>
          <p className="text-zinc-400 text-sm mt-1">Konverzácie zákazníkov s AI asistentom</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total_messages}</p>
              <p className="text-xs text-zinc-500">Celkové správy</p>
            </div>
          </div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <User className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total_sessions}</p>
              <p className="text-xs text-zinc-500">Konverzácií</p>
            </div>
          </div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.recent_messages}</p>
              <p className="text-xs text-zinc-500">Za posledných 7 dní</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Hľadať v konverzáciách..."
          className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#00BFFF]"
        />
      </div>

      {/* Chats Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Chat List */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h3 className="font-semibold">Konverzácie ({filteredChats.length})</h3>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {filteredChats.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">
                {searchTerm ? 'Žiadne výsledky' : 'Zatiaľ žiadne konverzácie'}
              </div>
            ) : (
              filteredChats.map((chat) => (
                <div
                  key={chat.session_id}
                  onClick={() => setSelectedChat(chat)}
                  className={`p-4 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${
                    selectedChat?.session_id === chat.session_id ? 'bg-white/5 border-l-2 border-l-purple-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4 text-purple-400" />
                        <span className="text-sm font-medium truncate">
                          {chat.messages[0]?.user?.substring(0, 40) || 'Konverzácia'}...
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1">
                        {chat.message_count} správ • {formatDate(chat.last_message_at)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteChat(chat.session_id); }}
                      className="p-1.5 hover:bg-red-500/20 text-zinc-500 hover:text-red-400 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Detail */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h3 className="font-semibold">Detail konverzácie</h3>
          </div>
          {selectedChat ? (
            <div className="max-h-[500px] overflow-y-auto p-4 space-y-4">
              {selectedChat.messages.map((msg, idx) => (
                <div key={idx} className="space-y-2">
                  {/* User message */}
                  <div className="flex justify-end">
                    <div className="max-w-[85%] p-3 bg-[#00BFFF]/20 rounded-2xl rounded-br-md">
                      <p className="text-sm">{msg.user}</p>
                      <p className="text-xs text-zinc-500 mt-1">{formatDate(msg.created_at)}</p>
                    </div>
                  </div>
                  {/* Bot response */}
                  <div className="flex justify-start">
                    <div className="max-w-[85%] p-3 bg-purple-500/20 rounded-2xl rounded-bl-md">
                      <div className="flex items-center gap-1 mb-1">
                        <Bot className="w-3 h-3 text-purple-400" />
                        <span className="text-xs text-purple-400">AI Asistent</span>
                      </div>
                      <p className="text-sm">{msg.bot}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-[500px] flex items-center justify-center text-zinc-500">
              Vyberte konverzáciu pre zobrazenie
            </div>
          )}
        </div>
      </div>

      {/* Common Questions Analysis */}
      {chats.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            Najčastejšie témy
          </h3>
          <p className="text-sm text-zinc-400">
            Analyzujte konverzácie a zistite, čo zákazníkov najviac zaujíma. Tieto informácie môžete využiť na vylepšenie FAQ sekcie alebo služieb.
          </p>
        </div>
      )}
    </div>
  );
};

// FAQ Tab
export const FAQTab = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ question: '', answer: '', order: 0, active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/admin/faq`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingFaq) {
        await axios.put(`${API}/admin/faq/${editingFaq.id}`, formData);
      } else {
        await axios.post(`${API}/admin/faq`, formData);
      }
      fetchFaqs();
      setShowForm(false);
      setEditingFaq(null);
      setFormData({ question: '', answer: '', order: 0, active: true });
    } catch (error) {
      console.error('Error saving FAQ:', error);
    } finally {
      setSaving(false);
    }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm('Naozaj chcete vymazať túto FAQ?')) return;
    try {
      await axios.delete(`${API}/admin/faq/${id}`);
      setFaqs(faqs.filter(f => f.id !== id));
    } catch (error) {
      console.error('Error deleting FAQ:', error);
    }
  };

  const startEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      order: faq.order,
      active: faq.active,
    });
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">FAQ</h1>
        <button
          data-testid="add-faq-btn"
          onClick={() => {
            setShowForm(true);
            setEditingFaq(null);
            setFormData({ question: '', answer: '', order: faqs.length, active: true });
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[#00BFFF] text-[#05050A] font-semibold rounded-lg hover:bg-[#33CCFF] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Pridať FAQ
        </button>
      </div>

      {/* FAQ Form */}
      {showForm && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingFaq ? 'Upraviť FAQ' : 'Nová FAQ'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Otázka
              </label>
              <input
                type="text"
                data-testid="faq-question-input"
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Odpoveď
              </label>
              <textarea
                data-testid="faq-answer-input"
                value={formData.answer}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF] min-h-[120px]"
                required
              />
            </div>
            <div className="flex items-center gap-6">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Poradie
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  className="w-24 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                />
              </div>
              <div className="flex items-center gap-2 mt-6">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-5 h-5 rounded border-white/20"
                />
                <label htmlFor="active" className="text-sm text-zinc-400">
                  Aktívna
                </label>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                data-testid="save-faq-btn"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-3 bg-[#00BFFF] text-[#05050A] font-semibold rounded-lg hover:bg-[#33CCFF] transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                Uložiť
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingFaq(null);
                }}
                className="px-6 py-3 border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
              >
                Zrušiť
              </button>
            </div>
          </form>
        </div>
      )}

      {/* FAQ List */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {faqs.length === 0 ? (
          <div className="p-8 text-center text-zinc-500">
            Žiadne FAQ položky
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                data-testid={`faq-item-${faq.id}`}
                className="p-4 hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="text-xs px-2 py-1 bg-white/10 rounded text-zinc-400">
                        #{faq.order}
                      </span>
                      {!faq.active && (
                        <span className="text-xs px-2 py-1 bg-yellow-500/10 text-yellow-400 rounded">
                          Neaktívna
                        </span>
                      )}
                    </div>
                    <h4 className="font-medium mt-2">{faq.question}</h4>
                    <p className="text-sm text-zinc-400 mt-1 line-clamp-2">
                      {faq.answer}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => startEdit(faq)}
                      className="p-2 hover:bg-white/10 text-zinc-400 hover:text-white rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteFaq(faq.id)}
                      className="p-2 hover:bg-red-500/10 text-zinc-400 hover:text-red-400 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Calendar Tab - Admin manages available time slots
export const CalendarTab = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('single'); // single or bulk
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    slot_type: 'obhliadka',
    duration_minutes: 30,
  });
  const [bulkData, setBulkData] = useState({
    startDate: '',
    endDate: '',
    times: ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00'],
    slot_type: 'obhliadka',
    duration_minutes: 30,
    excludeWeekends: true,
  });
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    try {
      const response = await axios.get(`${API}/admin/timeslots`);
      setSlots(response.data);
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/timeslots`, formData);
      fetchSlots();
      setShowForm(false);
      setFormData({ date: '', time: '', slot_type: 'obhliadka', duration_minutes: 30 });
    } catch (error) {
      alert(error.response?.data?.detail || 'Chyba pri vytváraní termínu');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Generate dates between start and end
      const dates = [];
      let current = new Date(bulkData.startDate);
      const end = new Date(bulkData.endDate);
      
      while (current <= end) {
        const dayOfWeek = current.getDay();
        if (!bulkData.excludeWeekends || (dayOfWeek !== 0 && dayOfWeek !== 6)) {
          dates.push(current.toISOString().split('T')[0]);
        }
        current.setDate(current.getDate() + 1);
      }

      const response = await axios.post(`${API}/admin/timeslots/bulk`, {
        dates,
        times: bulkData.times,
        slot_type: bulkData.slot_type,
        duration_minutes: bulkData.duration_minutes,
      });
      
      alert(`Vytvorených ${response.data.created} termínov`);
      fetchSlots();
      setShowForm(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Chyba pri vytváraní termínov');
    } finally {
      setSaving(false);
    }
  };

  const deleteSlot = async (id) => {
    if (!window.confirm('Naozaj chcete vymazať tento termín?')) return;
    try {
      await axios.delete(`${API}/admin/timeslots/${id}`);
      setSlots(slots.filter(s => s.id !== id));
    } catch (error) {
      alert(error.response?.data?.detail || 'Chyba pri mazaní');
    }
  };

  // Group slots by date
  const slotsByDate = slots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const availableTimes = ['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', 
                          '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Kalendár termínov</h1>
          <p className="text-zinc-400 text-sm mt-1">Nastavte voľné termíny pre obhliadky a realizácie</p>
        </div>
        <button
          data-testid="add-slot-btn"
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#00BFFF] text-[#05050A] font-semibold rounded-lg hover:bg-[#33CCFF] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Pridať termíny
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setFormType('single')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                formType === 'single' ? 'bg-[#00BFFF] text-[#05050A]' : 'bg-white/10 text-zinc-400'
              }`}
            >
              Jeden termín
            </button>
            <button
              onClick={() => setFormType('bulk')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                formType === 'bulk' ? 'bg-[#00BFFF] text-[#05050A]' : 'bg-white/10 text-zinc-400'
              }`}
            >
              Hromadne
            </button>
          </div>

          {formType === 'single' ? (
            <form onSubmit={handleSingleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Dátum</label>
                  <input
                    type="date"
                    data-testid="slot-date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Čas</label>
                  <select
                    data-testid="slot-time-input"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                    required
                  >
                    <option value="">Vyberte čas</option>
                    {availableTimes.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Typ termínu</label>
                  <select
                    value={formData.slot_type}
                    onChange={(e) => setFormData({ ...formData, slot_type: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                  >
                    <option value="obhliadka">Obhliadka</option>
                    <option value="realizacia">Realizácia</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Trvanie (min)</label>
                  <select
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                  >
                    <option value={30}>30 minút</option>
                    <option value={60}>60 minút</option>
                    <option value={90}>90 minút</option>
                    <option value={120}>120 minút</option>
                    <option value={180}>180 minút</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 bg-[#00BFFF] text-[#05050A] font-semibold rounded-lg hover:bg-[#33CCFF] transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                  Uložiť
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
                >
                  Zrušiť
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleBulkSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Od dátumu</label>
                  <input
                    type="date"
                    value={bulkData.startDate}
                    onChange={(e) => setBulkData({ ...bulkData, startDate: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Do dátumu</label>
                  <input
                    type="date"
                    value={bulkData.endDate}
                    onChange={(e) => setBulkData({ ...bulkData, endDate: e.target.value })}
                    min={bulkData.startDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Časy (vyberte viacero)</label>
                <div className="flex flex-wrap gap-2">
                  {availableTimes.map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        const newTimes = bulkData.times.includes(t) 
                          ? bulkData.times.filter(x => x !== t)
                          : [...bulkData.times, t];
                        setBulkData({ ...bulkData, times: newTimes });
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        bulkData.times.includes(t) 
                          ? 'bg-[#00BFFF] text-[#05050A]' 
                          : 'bg-white/10 text-zinc-400 hover:bg-white/20'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Typ termínu</label>
                  <select
                    value={bulkData.slot_type}
                    onChange={(e) => setBulkData({ ...bulkData, slot_type: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                  >
                    <option value="obhliadka">Obhliadka</option>
                    <option value="realizacia">Realizácia</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Trvanie</label>
                  <select
                    value={bulkData.duration_minutes}
                    onChange={(e) => setBulkData({ ...bulkData, duration_minutes: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#00BFFF]"
                  >
                    <option value={30}>30 minút</option>
                    <option value={60}>60 minút</option>
                    <option value={90}>90 minút</option>
                    <option value={120}>120 minút</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 mt-8">
                  <input
                    type="checkbox"
                    id="excludeWeekends"
                    checked={bulkData.excludeWeekends}
                    onChange={(e) => setBulkData({ ...bulkData, excludeWeekends: e.target.checked })}
                    className="w-5 h-5 rounded"
                  />
                  <label htmlFor="excludeWeekends" className="text-sm text-zinc-400">
                    Vynechať víkendy
                  </label>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={saving || bulkData.times.length === 0}
                  className="flex items-center gap-2 px-6 py-3 bg-[#00BFFF] text-[#05050A] font-semibold rounded-lg hover:bg-[#33CCFF] transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                  Vytvoriť termíny
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
                >
                  Zrušiť
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Slots Grid by Date */}
      <div className="space-y-4">
        {Object.keys(slotsByDate).length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center text-zinc-500">
            Žiadne termíny. Pridajte nové termíny pomocou tlačidla vyššie.
          </div>
        ) : (
          Object.entries(slotsByDate)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([date, dateSlots]) => (
              <div key={date} className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <CalendarDays className="w-5 h-5 text-[#00BFFF]" />
                    <span className="font-medium">
                      {new Date(date).toLocaleDateString('sk-SK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                  <span className="text-sm text-zinc-500">
                    {dateSlots.filter(s => s.is_available).length} voľných / {dateSlots.length} termínov
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {dateSlots
                    .sort((a, b) => a.time.localeCompare(b.time))
                    .map(slot => (
                      <div
                        key={slot.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                          slot.is_available 
                            ? slot.slot_type === 'obhliadka'
                              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                              : 'bg-green-500/20 text-green-400 border border-green-500/30'
                            : 'bg-zinc-500/20 text-zinc-500 border border-zinc-500/30'
                        }`}
                      >
                        <Clock className="w-4 h-4" />
                        <span>{slot.time}</span>
                        <span className="text-xs opacity-70">
                          ({slot.slot_type === 'obhliadka' ? 'O' : 'R'})
                        </span>
                        {slot.is_available && (
                          <button
                            onClick={() => deleteSlot(slot.id)}
                            className="ml-1 p-1 hover:bg-white/10 rounded"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            ))
        )}
      </div>

      {/* Legend */}
      <div className="flex gap-6 text-sm text-zinc-500">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-500/20 border border-blue-500/30" />
          <span>Obhliadka (O)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500/20 border border-green-500/30" />
          <span>Realizácia (R)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-zinc-500/20 border border-zinc-500/30" />
          <span>Obsadené</span>
        </div>
      </div>
    </div>
  );
};

// Bookings Tab - Admin views and manages bookings
export const BookingsTab = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${id}`, { status });
      setBookings(bookings.map(b => b.id === id ? { ...b, status } : b));
    } catch (error) {
      alert(error.response?.data?.detail || 'Chyba pri aktualizácii');
    }
  };

  const filteredBookings = filter === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === filter);

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
    cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
    completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };

  const statusLabels = {
    pending: 'Čakajúca',
    confirmed: 'Potvrdená',
    cancelled: 'Zrušená',
    completed: 'Dokončená',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#00BFFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Rezervácie</h1>
          <p className="text-zinc-400 text-sm mt-1">Správa rezervácií zákazníkov</p>
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                filter === f ? 'bg-[#00BFFF] text-[#05050A]' : 'bg-white/10 text-zinc-400 hover:bg-white/20'
              }`}
            >
              {f === 'all' ? 'Všetky' : statusLabels[f]}
            </button>
          ))}
        </div>
      </div>

      {filteredBookings.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center text-zinc-500">
          Žiadne rezervácie
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBookings.map(booking => (
            <div
              key={booking.id}
              data-testid={`booking-${booking.id}`}
              className="bg-white/5 border border-white/10 rounded-xl p-6"
            >
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[booking.status]}`}>
                      {statusLabels[booking.status]}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      booking.slot_type === 'obhliadka' 
                        ? 'bg-blue-500/20 text-blue-400' 
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {booking.slot_type === 'obhliadka' ? 'Obhliadka' : 'Realizácia'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-4 h-4 text-[#00BFFF]" />
                      <span>{new Date(booking.slot_date).toLocaleDateString('sk-SK')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#00BFFF]" />
                      <span>{booking.slot_time}</span>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-zinc-500" />
                      <span className="font-medium">{booking.customer_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${booking.customer_email}`} className="hover:text-[#00BFFF]">
                        {booking.customer_email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                      <Phone className="w-4 h-4" />
                      <a href={`tel:${booking.customer_phone}`} className="hover:text-[#00BFFF]">
                        {booking.customer_phone}
                      </a>
                    </div>
                  </div>

                  {booking.vehicle_info && (
                    <div className="flex items-start gap-2 text-sm text-zinc-400">
                      <Car className="w-4 h-4 mt-0.5" />
                      <span>{booking.vehicle_info}</span>
                    </div>
                  )}

                  {booking.notes && (
                    <div className="flex items-start gap-2 text-sm text-zinc-400">
                      <FileText className="w-4 h-4 mt-0.5" />
                      <span>{booking.notes}</span>
                    </div>
                  )}
                </div>

                {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                  <div className="flex gap-2">
                    {booking.status === 'pending' && (
                      <button
                        onClick={() => updateStatus(booking.id, 'confirmed')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Potvrdiť
                      </button>
                    )}
                    {booking.status === 'confirmed' && (
                      <button
                        onClick={() => updateStatus(booking.id, 'completed')}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                      >
                        <Check className="w-4 h-4" />
                        Dokončiť
                      </button>
                    )}
                    <button
                      onClick={() => updateStatus(booking.id, 'cancelled')}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      Zrušiť
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
